'use client'

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, InputGroup, ListGroup, Modal, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router";
import { format, addDays, subDays, parse } from 'date-fns';
import { ru } from 'date-fns/locale';
import Icon from '../../components/Icon';
import {
    faArrowLeft as arrowBack,
    faArrowRight as arrowForward,
    faCalendar as calendarOutline,
    faHistory as historyIcon,
    faTimes as close,
    faListAlt as unplannedIcon,
    faCheckCircle as checkmarkCircle,
    faTrash as trashBin,
    faBars as bars,
    faPlus as add,
    faRotateRight as refresh,
    faCalendarCheck as monthGoalsIcon,
} from '@fortawesome/free-solid-svg-icons';
import { TaskList, DayTaskCard, LifePartGroup } from "../../components/plan/TaskList";
import { getFirstDay } from "../../components/plan/tasksUtils";
import { PlanScreenContext, TaskModalProps } from "../../components/plan/context";
import { DayPlan } from "../../state/DayPlan";
import { useTaskModal } from "../../components/TaskModalContext";
import { FilterModal } from "../../components/plan/FilterModal";
import { useData } from "../../storage/accessors";
import { useDebounce } from "@uidotdev/usehooks";
import { useScreenSize } from "../../contexts/screenSizeContext";
import ButtonDatePicker from "../../components/datePicker";
import { TaskList as TaskListEntity } from "../../state/TaskList";
import { transparentize, deadlineColor } from "../../state/helpers";
import { AddTaskToListModal } from "../../components/AddTaskToListModal";
import { TaskListColumn } from "../../components/lists/TaskListColumn";
import { UnplannedTaskCard } from "../../components/lists/TaskListColumn";

// Helper function to capitalize the first letter of a string
const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export default function ActionsPage() {
    const screenSize = useScreenSize();
    const isMobile = screenSize === "MOBILE";
    const { setTaskModal: setPlanTaskModal } = useContext(PlanScreenContext);
    const taskModalContext = useTaskModal();
    const setTaskModal = taskModalContext?.setTaskModal;
    const [firstDay, setFirstDay] = useState(new Date());
    const [filterPart, setFilterPart] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [justDonePlans, setJustDonePlans] = useState<string[]>([]);
    const [hideDone, setHideDone] = useState(true);
    // Дни (yyyy-MM-dd), где отображение готовых задач переключено кликом по счётчику
    const [invertedDoneDays, setInvertedDoneDays] = useState<string[]>([]);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showOld, setShowOld] = useState(false);
    const [showUnplanned, setShowUnplanned] = useState(false);
    const [showLists, setShowLists] = useState(false);
    const [showMonthGoals, setShowMonthGoals] = useState(false);
    const [dragOverDate, setDragOverDate] = useState<string | null>(null);
    const [addTaskToList, setAddTaskToList] = useState<string | null>(null);
    const [showListSelector, setShowListSelector] = useState(false);
    const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
    const isCtrlPressed = useRef(false);
    const visibleDays = isMobile ? 1 : 7;
    // Для вычисления месяцев всегда используем полную неделю (7 дней)
    const weekDays = 7;
    // Заголовок: один месяц или диапазон (например "Январь — Февраль")
    const firstMonth = format(firstDay, 'LLLL', { locale: ru });
    const lastDayOfWeek = addDays(firstDay, visibleDays - 1);
    const lastMonth = format(lastDayOfWeek, 'LLLL', { locale: ru });
    const formattedMonth = firstMonth === lastMonth ? firstMonth : `${firstMonth} — ${lastMonth}`;
    const debounced = useDebounce(justDonePlans, 5000);

    const { roles, plans, tasks, taskLists } = useData();
    const lifeParts = Object.values(roles.objects);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollIntervalRef = useRef<number | null>(null);

    // Обертка для setTaskModal, которая использует оба контекста
    const handleOpenTask = useCallback((props: TaskModalProps) => {
        if (setTaskModal) {
            setTaskModal(props);
        }
    }, [setTaskModal]);

    // Получаем прошлые планы (незавершённые с датой меньше firstDay)
    const pastPlans = useMemo(() => {
        const firstDayStr = format(firstDay, 'yyyy-MM-dd');
        return Object.values(plans.objects)
            .filter(p => !p.isDone && p.date < firstDayStr)
            .sort((a, b) => a.date.localeCompare(b.date));
    }, [plans.objects, firstDay]);

    // Фильтрация прошлых планов по filterPart и searchTerm
    const filteredPastPlans = useMemo(() => {
        return pastPlans.filter(plan => {
            const task = tasks.objects[plan.task];
            if (!task) return false;

            // Фильтр по части жизни
            if (filterPart && task.lifepart !== filterPart) return false;

            // Фильтр по поиску
            if (searchTerm && !(task.description || "").toLowerCase().includes(searchTerm.toLowerCase())) return false;

            return true;
        });
    }, [pastPlans, tasks.objects, filterPart, searchTerm]);

    // Получаем незапланированные задачи (нет плана в будущем с isDone=false)
    const unplannedTasks = useMemo(() => {
        const startOfFuture = addDays(firstDay, -1);
        const startOfFutureStr = format(startOfFuture, 'yyyy-MM-dd');

        // Находим все активные планы в будущем (от следующей недели)
        const futurePlans = Object.values(plans.objects).filter(p => !p.isDone && p.date >= startOfFutureStr);
        const plannedTaskIds = new Set(futurePlans.map(p => p.task));

        // Находим все задачи, которые не имеют активных планов в будущем
        return Object.values(tasks.objects)
            .filter(task => !task.isDone && !plannedTaskIds.has(task.uuid));
    }, [tasks.objects, plans.objects, firstDay]);

    // Фильтрация незапланированных задач по filterPart и searchTerm
    const filteredUnplannedTasks = useMemo(() => {
        return unplannedTasks.filter(task => {
            // Фильтр по части жизни
            if (filterPart && task.lifepart !== filterPart) return false;

            // Фильтр по поиску
            if (searchTerm && !(task.description || "").toLowerCase().includes(searchTerm.toLowerCase())) return false;

            return true;
        });
    }, [unplannedTasks, filterPart, searchTerm]);

    // Получаем все месяцы, попадающие в отображаемую неделю
    const displayedMonthKeys = useMemo(() => {
        const months: string[] = [];
        for (let i = 0; i < weekDays; i++) {
            const day = addDays(firstDay, i);
            const monthKey = format(day, 'yyyy-MM');
            if (!months.includes(monthKey)) {
                months.push(monthKey);
            }
        }
        // Сортируем по возрастанию (сначала более ранний месяц)
        return months.sort();
    }, [firstDay]);

    // Задачи с месяцами, попадающими в отображаемую неделю
    const allMonthGoalTasks = useMemo(() => {
        return Object.values(tasks.objects)
            .filter(task => {
                // Не завершённые задачи
                if (task.isDone) return false;

                // Есть хотя бы один месяц в month
                if (!task.month || task.month.length === 0) return false;

                // Содержит хотя бы один из отображаемых месяцев
                if (!task.month.some(m => displayedMonthKeys.includes(m))) return false;

                // Применяем фильтры
                if (filterPart && task.lifepart !== filterPart) return false;
                if (searchTerm && !(task.description || "").toLowerCase().includes(searchTerm.toLowerCase())) return false;

                return true;
            });
    }, [tasks.objects, displayedMonthKeys, filterPart, searchTerm]);

    // Задачи для каждого отображаемого месяца
    const monthGoalTasksByMonth = useMemo(() => {
        return displayedMonthKeys.map(monthKey => {
            const tasksForMonth = allMonthGoalTasks.filter(task =>
                task.month && task.month.includes(monthKey)
            ).sort((a, b) => (a.order || 0) - (b.order || 0));

            const monthDate = new Date(monthKey + '-01');
            const monthName = capitalizeFirstLetter(format(monthDate, 'LLLL', { locale: ru }));

            return { monthKey, monthName, tasks: tasksForMonth };
        });
    }, [allMonthGoalTasks, displayedMonthKeys]);

    // Флаги для алёртов
    const filteredMonthGoalTasks = allMonthGoalTasks; // Уже отфильтровано выше

    // Проверка: можно ли перетащить на указанную дату (не раньше сегодня)
    const canDropOnDate = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const targetDate = new Date(date);
        targetDate.setHours(0, 0, 0, 0);
        return targetDate >= today;
    };

    const goPrevious = () => {
        if (isMobile) {
            setShowOld(false);
            setShowUnplanned(false);
        }

        setFirstDay(subDays(firstDay, visibleDays));
    };

    const goNext = () => {
        if (isMobile) {
            setShowOld(false);
            setShowUnplanned(false);
        }
        setFirstDay(addDays(firstDay, visibleDays));
    };

    const planJustDone = useCallback((uuid: string) => {
        setJustDonePlans(old => [...old, uuid]);
    }, []);

    // Клик по числу выполненных в колонке дня: локально (для одного дня)
    // переключает отображение готовых задач, не трогая глобальный фильтр.
    const toggleDoneDay = useCallback((dateKey: string) => {
        setInvertedDoneDays(prev => prev.includes(dateKey)
            ? prev.filter(d => d !== dateKey)
            : [...prev, dateKey]);
    }, []);

    // Смена глобального режима «показать готовые» делает локальные переключения
    // по дням неактуальными.
    const changeHideDone = useCallback((value: boolean) => {
        setHideDone(value);
        setInvertedDoneDays([]);
    }, []);

    const movePlan = useCallback((planUUID: string, newDate: string) => {
        plans.save(planUUID, { date: newDate });
    }, [plans]);

    const copyPlan = useCallback((planUUID: string, newDate: string) => {
        const currentPlan = Object.values(plans.objects).find(p => p.uuid === planUUID);
        if (!currentPlan) return;

        const task = tasks.objects[currentPlan.task];
        if (!task) return;

        // Create new plan on new date with same properties
        const newPlan = {
            uuid: crypto.randomUUID(),
            isDone: false,
            task: task.uuid,
            date: newDate,
            ordering: currentPlan.ordering,
            needTime: currentPlan.needTime || "00:00",
            isImportant: currentPlan.isImportant,
            isGoal: currentPlan.isGoal,
            startTime: currentPlan.startTime,
            endTime: currentPlan.endTime,
            createdAt: format(new Date(), 'yyyy-MM-dd'),
        };
        plans.create(newPlan);
    }, [plans, tasks]);

    // Создание плана из задачи (при drag & drop из незапланированных)
    const createPlanFromTask = useCallback((taskUUID: string, newDate: string) => {
        const task = tasks.objects[taskUUID];
        if (!task) return;

        const newPlan = {
            uuid: crypto.randomUUID(),
            isDone: false,
            task: taskUUID,
            date: newDate,
            ordering: 0,
            needTime: "00:00",
        };
        plans.create(newPlan);
    }, [plans, tasks]);

    // Получаем все списки задач
    const sortedLists = useMemo(() => {
        return Object.values(taskLists.objects).sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [taskLists.objects]);

    // Получаем выбранные списки для отображения
    const displayedLists = useMemo(() => {
        return sortedLists.filter(list => selectedListIds.includes(list.uuid));
    }, [sortedLists, selectedListIds]);

    // Получаем задачи для списка
    const getTasksForList = useCallback((listUuid: string) => {
        return Object.values(tasks.objects)
            .filter(t => t.taskList === listUuid && !t.isDone)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [tasks.objects]);

    // Удаление задачи из списка (очищаем taskList)
    const removeTaskFromList = useCallback((taskUUID: string) => {
        tasks.save(taskUUID, { taskList: '' });
    }, [tasks]);

    // Обновление порядка задач в списке
    const updateTaskOrderInList = useCallback((taskUuid: string, targetIndex: number, listUuid: string) => {
        // Получаем актуальный список задач
        const currentListTasks = Object.values(tasks.objects)
            .filter(t => t.taskList === listUuid)
            .sort((a, b) => (a.order || 0) - (b.order || 0));

        const currentIndex = currentListTasks.findIndex(t => t.uuid === taskUuid);
        if (currentIndex !== -1 && currentIndex !== targetIndex) {
            // Создаём новый порядок задач
            const newOrder = [...currentListTasks.map(t => t.uuid)];
            const [removed] = newOrder.splice(currentIndex, 1);
            newOrder.splice(targetIndex, 0, removed);

            // Обновляем порядок всех задач в списке
            newOrder.forEach((uuid, index) => {
                tasks.save(uuid, { order: index });
            });
        }
    }, [tasks]);

    // Добавление списка для отображения
    const addListToDisplay = useCallback((listId: string) => {
        setSelectedListIds(prev => [...prev, listId]);
    }, []);

    // Удаление списка с отображения (не удаляя сам список)
    const removeListFromDisplay = useCallback((listId: string) => {
        setSelectedListIds(prev => prev.filter(id => id !== listId));
    }, []);

    useEffect(() => {
        if (debounced.length > 0) {
            setJustDonePlans([]);
        }
    }, [debounced])

    useEffect(() => {
        setFirstDay(isMobile ? new Date() : getFirstDay())
    }, [isMobile])

    // Отслеживаем Ctrl/Cmd глобально для live-режима (зажатие во время drag)
    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.ctrlKey || e.metaKey) isCtrlPressed.current = true;
        };
        const up = (e: KeyboardEvent) => {
            if (!e.ctrlKey && !e.metaKey) isCtrlPressed.current = false;
        };
        window.addEventListener('keydown', down);
        window.addEventListener('keyup', up);
        return () => {
            window.removeEventListener('keydown', down);
            window.removeEventListener('keyup', up);
        };
    }, []);

    // Очистка интервала скролла при размонтировании
    useEffect(() => {
        return () => {
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
            }
        };
    }, []);

    // Горизонтальный скролл при drag-over на краях контейнера
    const handleDragOverWithScroll = (e: React.DragEvent, targetDate: Date, key: string) => {
        if (isMobile) return;

        const container = scrollContainerRef.current;
        if (!container) return;

        // Всегда делаем preventDefault для разрешения drop
        e.preventDefault();

        // Проверка даты для визуального эффекта
        if (!canDropOnDate(targetDate)) {
            e.dataTransfer.dropEffect = 'none';
        } else {
            // Определяем тип перетаскивания
            const dataType = e.dataTransfer.types.includes('task') ? 'task' : 'plan';
            const wantsCopy = e.dataTransfer.types.includes('text/copy');
            const liveCopy = isCtrlPressed.current;
            e.dataTransfer.dropEffect = dataType === 'task' ? 'move' : ((wantsCopy || liveCopy) ? 'copy' : 'move');
            setDragOverDate(key);
        }

        // Скролл при наведении на края (независимо от допустимости даты)
        const rect = container.getBoundingClientRect();
        const scrollThreshold = 200;
        const scrollSpeed = 10;

        // Очищаем предыдущий интервал
        if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
        }

        if (e.clientX < rect.left + scrollThreshold) {
            // Скролл влево
            scrollIntervalRef.current = window.setInterval(() => {
                container.scrollLeft -= scrollSpeed;
            }, 16);
        } else if (e.clientX > rect.right - scrollThreshold) {
            // Скролл вправо
            scrollIntervalRef.current = window.setInterval(() => {
                container.scrollLeft += scrollSpeed;
            }, 16);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        if (isMobile) return;
        // Очищаем интервал скролла
        if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;
        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setDragOverDate(null);
        }
    };

    const handleDrop = (e: React.DragEvent, targetDate: Date, key: string) => {
        if (isMobile) return;

        // Очищаем интервал скролла
        if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
        }

        e.preventDefault();
        setDragOverDate(null);

        const dataType = e.dataTransfer.types.includes('task') ? 'task' : 'plan';

        // Проверка даты - нельзя перетащить на прошлую дату
        if (!canDropOnDate(targetDate)) {
            return; // Блокируем drop на прошлую дату
        }

        if (dataType === 'task') {
            // Перетаскивание задачи из незапланированных или из списка
            const taskUUID = e.dataTransfer.getData('task');
            const sourceList = e.dataTransfer.getData('sourceList');
            if (taskUUID) {
                createPlanFromTask(taskUUID, key);
                // Задачу из списка НЕ удаляем - она остаётся в списке
            }
        } else {
            // Перетаскивание плана (существующего)
            const planUUID = e.dataTransfer.getData('text/plain');
            if (!planUUID) return;

            const isCopy = e.dataTransfer.types.includes('text/copy') || isCtrlPressed.current;
            if (isCopy) {
                copyPlan(planUUID, key);
            } else {
                movePlan(planUUID, key);
            }
        }
    };

    return <PlanScreenContext.Provider value={{ setTaskModal: handleOpenTask, filterPart, searchTerm, hideDone, showOld, justDonePlans, invertedDoneDays, planJustDone, movePlan, copyPlan, toggleDoneDay }}>
        {lifeParts.length === 0 ? (
            <Alert variant="warning">
                Сначала создайте части жизни на экране «Баланс» <Link className="btn btn-primary btn-sm" to="./roles">Перейти</Link>
            </Alert>
        ) : (
            <>
                <Row className="align-items-center mb-3">
                    <Col md={12}>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <h2 style={{ cursor: "pointer", display: "inline", margin: 0 }} onClick={() => {
                                if (isMobile) {
                                    setShowOld(false);
                                    setShowUnplanned(false);
                                }
                                setFirstDay(isMobile ? new Date() : getFirstDay());
                            }}>{formattedMonth}</h2>

                            <div className="d-flex align-items-center gap-1">
                                <Button variant="outline-secondary" className="rounded-circle p-2" onClick={goPrevious} style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon icon={arrowBack} />
                                </Button>
                                {isMobile && (
                                    <ButtonDatePicker
                                        date={firstDay}
                                        onSelect={(date) => {
                                            setShowOld(false);
                                            setShowUnplanned(false);
                                            date && setFirstDay(date);
                                        }}
                                        icon={calendarOutline}
                                        title="Выбрать дату"
                                        inputProps={{ variant: "outline-secondary", style: {} }}
                                    />
                                )}
                                <Button variant="outline-secondary" className="rounded-circle p-2" onClick={goNext} style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Icon icon={arrowForward} />
                                </Button>
                            </div>

                            <Button variant="outline-secondary" onClick={() => setShowFilterModal(true)}>
                                Фильтры
                            </Button>

                            <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    setShowOld(!showOld);
                                    if (!showOld && isMobile) setShowUnplanned(false)
                                }}
                                title={showOld ? 'Скрыть прошлые' : 'Показать прошлые'}
                                className={isMobile ? "p-2" : ""}
                                style={isMobile ? { width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}
                            >
                                <Icon icon={historyIcon} />
                                {!isMobile && <span className="ms-2">{showOld ? 'Скрыть прошлые' : 'Показать прошлые'}</span>}
                            </Button>

                            <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    setShowUnplanned(!showUnplanned);
                                    if (!showUnplanned && isMobile) setShowOld(false)
                                }}
                                title={showUnplanned ? 'Скрыть незапланированные' : 'Показать незапланированные'}
                                className={isMobile ? "p-2" : ""}
                                style={isMobile ? { width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}
                            >
                                <Icon icon={unplannedIcon} />
                                {!isMobile && <span className="ms-2">{showUnplanned ? 'Скрыть незапланированные' : 'Не запланировано'}</span>}
                            </Button>

                            <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    setShowMonthGoals(!showMonthGoals);
                                    if (!showMonthGoals && isMobile) {
                                        setShowOld(false);
                                        setShowUnplanned(false);
                                    }
                                }}
                                title={showMonthGoals ? 'Скрыть цели на месяц' : 'Показать цели на месяц'}
                                className={isMobile ? "p-2" : ""}
                                style={isMobile ? { width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}
                            >
                                <Icon icon={monthGoalsIcon} />
                                {!isMobile && <span className="ms-2">{showMonthGoals ? 'Скрыть цели на месяц' : 'Цели на месяц'}</span>}
                            </Button>

                            {!isMobile && (
                                <Button
                                    variant="outline-secondary"
                                    onClick={() => {
                                        setShowListSelector(true);
                                    }}
                                    title="Выбрать списки"
                                >
                                    <Icon icon={bars} />
                                    <span className="ms-2">Списки</span>
                                    {selectedListIds.length > 0 && (
                                        <Badge bg="primary" className="ms-2">{selectedListIds.length}</Badge>
                                    )}
                                </Button>
                            )}

                            {filterPart && (
                                <Badge bg="primary" className="d-flex align-items-center gap-1">
                                    {Object.values(roles?.objects || {}).find(p => p.uuid === filterPart)?.name}
                                    <Icon icon={close} style={{ cursor: 'pointer' }} onClick={() => setFilterPart('')} />
                                </Badge>
                            )}

                            {searchTerm && (
                                <Badge bg="info" className="d-flex align-items-center gap-1">
                                    {searchTerm}
                                    <Icon icon={close} style={{ cursor: 'pointer' }} onClick={() => setSearchTerm('')} />
                                </Badge>
                            )}

                            {!hideDone && (
                                <Badge bg="warning" className="d-flex align-items-center gap-1 text-dark">
                                    Показать готовые
                                    <Icon icon={close} style={{ cursor: 'pointer' }} onClick={() => changeHideDone(true)} />
                                </Badge>
                            )}
                        </div>
                    </Col>
                </Row>
                <div
                    ref={scrollContainerRef}
                    style={{
                        gap: '16px'
                    }}
                    className={`d-flex flex-nowrap overflow-x-auto visible-scrollbar ${isMobile && (showOld || showUnplanned || showMonthGoals) ? 'flex-column' : ''}`}
                    onMouseLeave={() => {
                        // Очищаем скролл при выходе мыши из контейнера
                        if (scrollIntervalRef.current) {
                            clearInterval(scrollIntervalRef.current);
                            scrollIntervalRef.current = null;
                        }
                    }}
                >
                    {/* Отображение списков задач слева - только для десктопа */}
                    {!isMobile && showLists && displayedLists.map(list => {
                        const listTasks = getTasksForList(list.uuid);
                        return (
                            <TaskListColumn
                                key={list.uuid}
                                taskList={list}
                                tasks={listTasks}
                                onRemoveTask={removeTaskFromList}
                                onReorderTasks={updateTaskOrderInList}
                                isMobile={isMobile}
                                onOpenTask={(taskUuid) => handleOpenTask({ taskUUID: taskUuid, fromDate: new Date() })}
                            />
                        );
                    })}

                    {showOld && (
                        <Col
                            key="past"
                            style={{
                                minWidth: isMobile ? '100%' : 330,
                                maxWidth: isMobile ? '100%' : undefined,
                                paddingRight: "calc(var(--bs-gutter-x) * .5)",
                                paddingLeft: "calc(var(--bs-gutter-x) * .5)",
                                flexShrink: 0,
                            }}
                        >
                            <ul className="list-group list-group-flush">
                                <li key="past-header" className="list-group-item d-flex align-items-end">
                                    <div className="text-center w-100 d-flex flex-row justify-content-between">
                                        <div><strong>Прошлые</strong> <Badge bg="secondary">{filteredPastPlans.length}</Badge></div>
                                    </div>
                                </li>
                                {/* Группировка прошлых планов по сферам жизни */}
                                <LifePartGroup
                                    items={filteredPastPlans}
                                    getLifePartUuid={(plan) => tasks.objects[(plan as DayPlan).task]?.lifepart || ''}
                                    renderPlan={(plan) => (
                                        <DayTaskCard
                                            key={plan.uuid}
                                            plan={plan}
                                            date={parse(plan.date, 'yyyy-MM-dd', new Date())}
                                            otherDayPlans={filteredPastPlans}
                                        />
                                    )}
                                    roles={roles.objects}
                                />
                                {filteredPastPlans.length === 0 && pastPlans.length > 0 && <Alert className="mt-3" variant="info">Нет задач по выбранным фильтрам</Alert>}
                                {filteredPastPlans.length === 0 && pastPlans.length === 0 && <Alert className="mt-3" variant="success">Прошлых карточек нет</Alert>}
                            </ul>
                        </Col>
                    )}

                    {showUnplanned && (
                        <Col
                            key="unplanned"
                            style={{
                                minWidth: isMobile ? '100%' : 330,
                                maxWidth: isMobile ? '100%' : undefined,
                                paddingRight: "calc(var(--bs-gutter-x) * .5)",
                                paddingLeft: "calc(var(--bs-gutter-x) * .5)",
                                flexShrink: 0,
                            }}
                        >
                            <ul className="list-group list-group-flush">
                                <li key="unplanned-header" className="list-group-item d-flex align-items-end">
                                    <div className="text-center w-100 d-flex flex-row justify-content-between">
                                        <div><strong>Не запланировано</strong> <Badge bg="secondary">{filteredUnplannedTasks.length}</Badge></div>
                                    </div>
                                </li>
                                {/* Группировка незапланированных задач по сферам жизни */}
                                <LifePartGroup
                                    items={filteredUnplannedTasks}
                                    getLifePartUuid={(task) => (task as { lifepart: string }).lifepart}
                                    renderUnplanned={(task) => {
                                        const fullTask = filteredUnplannedTasks.find(t => t.uuid === task.uuid);
                                        if (!fullTask) return null;
                                        const part = roles?.objects?.[fullTask.lifepart];
                                        const partColor = part?.color || "#FFFFFF";

                                        const handleDragStart = (e: React.DragEvent) => {
                                            if (isMobile) {
                                                e.preventDefault();
                                                return;
                                            }
                                            e.dataTransfer.setData('task', fullTask.uuid);
                                            e.dataTransfer.effectAllowed = 'move';
                                            (e.target as HTMLElement).style.opacity = '0.5';
                                        };

                                        const handleDragEnd = (e: React.DragEvent) => {
                                            (e.target as HTMLElement).style.opacity = '1';
                                        };

                                        return (
                                            <UnplannedTaskCard
                                                key={fullTask.uuid}
                                                task={fullTask}
                                                isMobile={isMobile}
                                                onOpenTask={() => handleOpenTask({ taskUUID: fullTask.uuid, fromDate: new Date() })}
                                                onToggleDone={() => {
                                                    tasks.save(fullTask.uuid, { isDone: !fullTask.isDone });
                                                }}
                                                onDelete={() => {
                                                    if (confirm('Удалить задачу?')) {
                                                        tasks.delete(fullTask.uuid);
                                                    }
                                                }}
                                                onDragStart={handleDragStart}
                                                onDragEnd={handleDragEnd}
                                            />
                                        );
                                    }}
                                    roles={roles.objects}
                                />
                                {filteredUnplannedTasks.length === 0 && unplannedTasks.length > 0 && <Alert className="mt-3" variant="info">Нет задач по выбранным фильтрам</Alert>}
                                {filteredUnplannedTasks.length === 0 && unplannedTasks.length === 0 && <Alert className="mt-3" variant="success">Все задачи запланированы</Alert>}
                            </ul>
                        </Col>
                    )}

                    {showMonthGoals && (
                        <Col
                            key="monthGoals"
                            style={{
                                minWidth: isMobile ? '100%' : 330,
                                maxWidth: isMobile ? '100%' : undefined,
                                paddingRight: "calc(var(--bs-gutter-x) * .5)",
                                paddingLeft: "calc(var(--bs-gutter-x) * .5)",
                                flexShrink: 0,
                            }}
                        >
                            <ul className="list-group list-group-flush">
                                {monthGoalTasksByMonth.map((monthGroup) => (
                                    <React.Fragment key={monthGroup.monthKey}>
                                        <li key={`monthGoals-header-${monthGroup.monthKey}`} className="list-group-item d-flex align-items-end">
                                            <div className="text-center w-100 d-flex flex-row justify-content-between">
                                                <div><strong>Цели на {monthGroup.monthName}</strong> <Badge bg="secondary">{monthGroup.tasks.length}</Badge></div>
                                            </div>
                                        </li>
                                        {/* Группировка целей на месяц по сферам жизни */}
                                        <LifePartGroup
                                            items={monthGroup.tasks}
                                            getLifePartUuid={(task) => (task as { lifepart: string }).lifepart}
                                            renderUnplanned={(task) => {
                                                const fullTask = monthGroup.tasks.find(t => t.uuid === task.uuid);
                                                if (!fullTask) return null;

                                                const handleDragStart = (e: React.DragEvent) => {
                                                    if (isMobile) {
                                                        e.preventDefault();
                                                        return;
                                                    }
                                                    e.dataTransfer.setData('task', fullTask.uuid);
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    (e.target as HTMLElement).style.opacity = '0.5';
                                                };

                                                const handleDragEnd = (e: React.DragEvent) => {
                                                    (e.target as HTMLElement).style.opacity = '1';
                                                };

                                                return (
                                                    <UnplannedTaskCard
                                                        key={fullTask.uuid}
                                                        task={fullTask}
                                                        isMobile={isMobile}
                                                        onOpenTask={() => handleOpenTask({ taskUUID: fullTask.uuid, fromDate: new Date() })}
                                                        onToggleDone={() => {
                                                            tasks.save(fullTask.uuid, { isDone: !fullTask.isDone });
                                                        }}
                                                        onDelete={() => {
                                                            if (confirm('Удалить задачу?')) {
                                                                tasks.delete(fullTask.uuid);
                                                            }
                                                        }}
                                                        onDragStart={handleDragStart}
                                                        onDragEnd={handleDragEnd}
                                                    />
                                                );
                                            }}
                                            roles={roles.objects}
                                        />
                                        {monthGroup.tasks.length === 0 && <Alert className="mt-3" variant="success">Нет целей на {monthGroup.monthName}</Alert>}
                                    </React.Fragment>
                                ))}
                            </ul>
                        </Col>
                    )}
                    {(!isMobile || !(showOld || showUnplanned)) && Array.from({ length: visibleDays }).map((_, index) => {
                        const startDate = addDays(firstDay, index);
                        const key = format(startDate, "yyyy-MM-dd");
                        const today = format(new Date(), "yyyy-MM-dd");
                        const isCurrentDay = !isMobile && key === today;
                        // console.log('show day column', startDate, firstDay);
                        return (
                            <Col
                                key={key}
                                style={{
                                    minWidth: visibleDays == 7 ? 330 : undefined,
                                    paddingRight: "calc(var(--bs-gutter-x) * .5)",
                                    paddingLeft: "calc(var(--bs-gutter-x) * .5)",
                                    opacity: dragOverDate === key ? 0.7 : 1,
                                    transition: 'opacity 0.2s ease',
                                    backgroundColor: dragOverDate === key ? 'rgba(102, 126, 234, 0.15)' : 'transparent',
                                    borderRadius: '8px',
                                    border: isCurrentDay ? '2px solid #0d6efd' : '2px solid transparent',
                                }}
                                onDragOver={(e) => handleDragOverWithScroll(e, startDate, key)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, startDate, key)}
                            >
                                <TaskList date={startDate} />
                            </Col>
                        );
                    })}
                </div>

                {/* Модальное окно для добавления задачи в список */}
                {addTaskToList && (
                    <AddTaskToListModal
                        show={!!addTaskToList}
                        onHide={() => setAddTaskToList(null)}
                        taskListUuid={addTaskToList}
                    />
                )}

                {/* Модальное окно для выбора списков */}
                <Modal show={showListSelector} onHide={() => setShowListSelector(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Выберите списки</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="d-flex flex-column gap-2">
                            {sortedLists.length === 0 && (
                                <div className="text-center text-muted py-3">
                                    <small>Нет доступных списков</small>
                                </div>
                            )}
                            {sortedLists.map(list => (
                                <div
                                    key={list.uuid}
                                    className={`d-flex align-items-center gap-2 p-2 border rounded ${selectedListIds.includes(list.uuid) ? 'bg-primary-subtle' : ''}`}
                                    style={{
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => {
                                        if (selectedListIds.includes(list.uuid)) {
                                            // Уже выбран - снимаем выделение
                                            setSelectedListIds(selectedListIds.filter(id => id !== list.uuid));
                                        } else {
                                            // Не выбран - добавляем
                                            addListToDisplay(list.uuid);
                                        }
                                    }}
                                >
                                    <div
                                        className="rounded"
                                        style={{
                                            width: 24,
                                            height: 24,
                                            backgroundColor: list.color,
                                            flexShrink: 0
                                        }}
                                    />
                                    <span className="flex-grow-1">{list.name}</span>
                                    {selectedListIds.includes(list.uuid) && (
                                        <Badge bg="primary" onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedListIds(selectedListIds.filter(id => id !== list.uuid));
                                        }} style={{ cursor: 'pointer' }}>
                                            ✕ Снять
                                        </Badge>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-between">
                        {selectedListIds.length > 0 && (
                            <Button
                                variant="outline-danger"
                                onClick={() => setSelectedListIds([])}
                            >
                                Сбросить
                            </Button>
                        )}
                        <Button
                            className="ms-auto"
                            variant="primary"
                            onClick={() => {
                                setShowListSelector(false);
                                setShowLists(selectedListIds.length > 0);
                            }}
                        >
                            Готово
                        </Button>
                    </Modal.Footer>
                </Modal>

                <FilterModal
                    show={showFilterModal}
                    onHide={() => setShowFilterModal(false)}
                    filterPart={filterPart}
                    setFilterPart={setFilterPart}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    hideDone={hideDone}
                    setHideDone={changeHideDone}
                    roles={roles}
                />
            </>
        )}
    </PlanScreenContext.Provider>
}